import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Breadcrumbs, Link, Stack, Typography } from "@mui/material";
import {
  hierarchy,
  treemap,
  treemapSquarify,
  type HierarchyRectangularNode,
} from "d3-hierarchy";
import {
  buildDrilldownSubtopicRoot,
  hierarchyToTreemapData,
  TOPIC_CHART_COLORS,
  topicHasDrilldownSubtopics,
  type DominanceLevel,
  type TopicHierarchyNode,
  type TreemapDatum,
} from "./taxonomyHierarchy";
import type { TreemapSubtopicSelection } from "./subtopicChunkMatch";

const CHAR_WIDTH_RATIO = 0.58;

type LayoutRect = {
  datum: TreemapDatum;
  x0: number;
  y0: number;
  x1: number;
  y1: number;
};

function wrapLabelToLines(
  label: string,
  maxWidth: number,
  fontSize: number,
): string[] {
  const usableWidth = Math.max(24, maxWidth - 12);
  const maxCharsPerLine = Math.max(
    4,
    Math.floor(usableWidth / (fontSize * CHAR_WIDTH_RATIO)),
  );

  const words = label.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return [];

  const lines: string[] = [];
  let current = words[0]!;

  for (const word of words.slice(1)) {
    const candidate = `${current} ${word}`;
    if (candidate.length <= maxCharsPerLine) {
      current = candidate;
    } else {
      lines.push(current);
      current = word;
    }
  }
  lines.push(current);

  return lines.map((line) =>
    line.length > maxCharsPerLine
      ? `${line.slice(0, maxCharsPerLine - 1)}…`
      : line,
  );
}

function makeTreemapLayout(width: number, height: number) {
  return treemap<TreemapDatum>()
    .size([width, height])
    .tile(treemapSquarify.ratio(1))
    .paddingOuter(3)
    .paddingInner(1)
    .round(true);
}

function hierarchyToRects(
  rootData: TreemapDatum,
  width: number,
  height: number,
): LayoutRect[] {
  const root = hierarchy(rootData)
    .sum((d) => d.size)
    .sort((a, b) => (b.value ?? 0) - (a.value ?? 0));

  makeTreemapLayout(width, height)(root);

  return (root.children ?? []).map((node) => {
    const rect = node as HierarchyRectangularNode<TreemapDatum>;
    return {
      datum: rect.data,
      x0: rect.x0,
      y0: rect.y0,
      x1: rect.x1,
      y1: rect.y1,
    };
  });
}

/** Topic treemap layout — one squarified level of topic leaves. */
function layoutTopicTreemap(
  rootData: TreemapDatum,
  width: number,
  height: number,
): LayoutRect[] {
  const topicRoot: TreemapDatum = {
    name: "root",
    slug: "root",
    display: "Episode",
    size: 0,
    kind: "root",
    children:
      rootData.children?.map(({ children: _children, ...topic }) => topic) ??
      [],
  };

  return hierarchyToRects(topicRoot, width, height);
}

/** Match topic treemap regions, then squarify subtopics inside each topic box. */
function layoutGroupedSubtopicTreemap(
  topicData: TreemapDatum,
  subtopicData: TreemapDatum,
  width: number,
  height: number,
): { leaves: LayoutRect[]; branches: LayoutRect[] } {
  const topicRects = layoutTopicTreemap(topicData, width, height);
  const leaves: LayoutRect[] = [];

  for (const topicRect of topicRects) {
    const subtopics =
      subtopicData.children?.find(
        (topic) => topic.slug === topicRect.datum.slug,
      )?.children ?? [];
    if (subtopics.length === 0) continue;

    const innerWidth = topicRect.x1 - topicRect.x0;
    const innerHeight = topicRect.y1 - topicRect.y0;
    if (innerWidth < 4 || innerHeight < 4) continue;

    const subRoot: TreemapDatum = {
      name: topicRect.datum.display,
      slug: topicRect.datum.slug,
      display: topicRect.datum.display,
      size: 0,
      kind: "root",
      children: subtopics,
    };

    const subRects = hierarchyToRects(subRoot, innerWidth, innerHeight);
    for (const subRect of subRects) {
      leaves.push({
        datum: subRect.datum,
        x0: topicRect.x0 + subRect.x0,
        y0: topicRect.y0 + subRect.y0,
        x1: topicRect.x0 + subRect.x1,
        y1: topicRect.y0 + subRect.y1,
      });
    }
  }

  return { leaves, branches: topicRects };
}

function layoutTreemapNodes(
  rootData: TreemapDatum,
  width: number,
  height: number,
): HierarchyRectangularNode<TreemapDatum>[] {
  const root = hierarchy(rootData)
    .sum((d) => (d.children && d.children.length > 0 ? 0 : d.size))
    .sort((a, b) => (b.value ?? 0) - (a.value ?? 0));

  makeTreemapLayout(width, height)(root);

  return root
    .descendants()
    .filter(
      (node) => node.depth > 0,
    ) as HierarchyRectangularNode<TreemapDatum>[];
}

function TreemapLeaf({
  rect,
  onClick,
  interactive,
  compact,
  selected,
}: {
  rect: LayoutRect;
  onClick?: () => void;
  interactive?: boolean;
  compact?: boolean;
  selected?: boolean;
}) {
  const { datum, x0, y0, x1, y1 } = rect;
  const width = x1 - x0;
  const height = y1 - y0;
  if (width < 3 || height < 3) return null;

  const label = datum.display ?? datum.name;
  const fill = datum.fill ?? "#8884d8";
  const fontSize = compact
    ? datum.kind === "topic"
      ? 9
      : 8
    : datum.kind === "topic"
      ? 12
      : 10;
  const lineHeight = fontSize + 3;
  const showCount = width > 36 && height > 36;
  const countReserved = showCount ? 16 : 6;
  const labelAreaHeight = Math.max(0, height - 8 - countReserved);
  const maxLines = Math.max(1, Math.floor(labelAreaHeight / lineHeight));
  const showLabel = width > 40 && labelAreaHeight >= lineHeight;
  const lines = showLabel
    ? wrapLabelToLines(label, width, fontSize).slice(0, maxLines)
    : [];

  return (
    <g
      onClick={onClick}
      onKeyDown={(event) => {
        if (interactive && (event.key === "Enter" || event.key === " ")) {
          event.preventDefault();
          onClick?.();
        }
      }}
      role={interactive ? "button" : undefined}
      tabIndex={interactive ? 0 : undefined}
      style={{ cursor: interactive ? "pointer" : undefined, outline: "none" }}
    >
      <rect
        x={x0}
        y={y0}
        width={width}
        height={height}
        fill={fill}
        stroke={selected ? "#212121" : "#fff"}
        strokeWidth={selected ? 3 : 2}
        rx={2}
      />
      {lines.length > 0 && (
        <text
          x={x0 + 6}
          y={y0 + 6 + fontSize}
          fill="#fff"
          fontSize={fontSize}
          fontWeight={datum.kind === "topic" ? 600 : 500}
          pointerEvents="none"
        >
          {lines.map((line, index) => (
            <tspan
              key={`${line}-${index}`}
              x={x0 + 6}
              dy={index === 0 ? 0 : lineHeight}
            >
              {line}
            </tspan>
          ))}
        </text>
      )}
      {showCount && (
        <text
          x={x0 + 6}
          y={y0 + height - 8}
          fill="rgba(255,255,255,0.9)"
          fontSize={10}
          pointerEvents="none"
        >
          {datum.size}
        </text>
      )}
      <title>
        {label} ({datum.slug}) — {datum.size} mention
        {datum.size === 1 ? "" : "s"}
        {datum.parentTopicDisplay ? ` · ${datum.parentTopicDisplay}` : ""}
        {interactive ? " · Click for details" : ""}
      </title>
    </g>
  );
}

function TreemapBranch({ rect }: { rect: LayoutRect }) {
  const { datum, x0, y0, x1, y1 } = rect;
  const width = x1 - x0;
  const height = y1 - y0;
  if (width < 3 || height < 3) return null;

  const label = datum.display ?? datum.name;
  const fill = datum.fill ?? "#8884d8";
  const showTopicLabel = width > 56 && height > 24;

  return (
    <g pointerEvents="none">
      <rect
        x={x0}
        y={y0}
        width={width}
        height={height}
        fill="none"
        stroke={fill}
        strokeWidth={3}
        rx={3}
      />
      {showTopicLabel && (
        <text
          x={x0 + 5}
          y={y0 + 13}
          fill={fill}
          fontSize={11}
          fontWeight={700}
          pointerEvents="none"
        >
          {label.length > Math.floor(width / 7)
            ? `${label.slice(0, Math.floor(width / 7) - 1)}…`
            : label}
        </text>
      )}
      <title>
        {label} ({datum.slug}) — {datum.size} mentions
      </title>
    </g>
  );
}

type TopicTreemapProps = {
  hierarchy: TopicHierarchyNode[];
  /** Used when drillDown is false (legacy flat subtopic view). */
  level?: DominanceLevel;
  /** Click a topic to drill into its subtopics. Default true. */
  drillDown?: boolean;
  height?: number;
  /** Shared colors keyed by topic slug (from aggregate ranking). */
  colorBySlug?: Map<string, string>;
  /** Smaller labels; hides breadcrumb/caption when drillDown is on. */
  compact?: boolean;
  /** Called when a subtopic cell is clicked in drill-down view. */
  onSubtopicClick?: (selection: TreemapSubtopicSelection) => void;
  selectedSubtopicSlug?: string | null;
  /** When true, topics without subtopics open chunks via onSubtopicClick (general bucket). */
  leafTopicOpensChunks?: boolean;
  /** Fired when drill-down topic changes (including back to all topics). */
  onDrillTopicChange?: (topicSlug: string | null) => void;
};

const TopicTreemap: React.FC<TopicTreemapProps> = ({
  hierarchy,
  level = "topic",
  drillDown = true,
  height = 420,
  colorBySlug,
  compact = false,
  onSubtopicClick,
  selectedSubtopicSlug = null,
  leafTopicOpensChunks = false,
  onDrillTopicChange,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  const [drillTopicSlug, setDrillTopicSlug] = useState<string | null>(null);

  const topicBySlug = useMemo(
    () => new Map(hierarchy.map((topic) => [topic.slug, topic])),
    [hierarchy],
  );

  const topicData = useMemo(
    () =>
      hierarchyToTreemapData(
        hierarchy,
        TOPIC_CHART_COLORS,
        "topic",
        colorBySlug,
      ),
    [hierarchy, colorBySlug],
  );

  const subtopicData = useMemo(
    () =>
      hierarchyToTreemapData(
        hierarchy,
        TOPIC_CHART_COLORS,
        "subtopic",
        colorBySlug,
      ),
    [hierarchy, colorBySlug],
  );

  const drillSubtopicRoot = useMemo(() => {
    if (!drillTopicSlug) return null;
    return buildDrilldownSubtopicRoot(
      hierarchy,
      drillTopicSlug,
      TOPIC_CHART_COLORS,
      colorBySlug,
    );
  }, [hierarchy, drillTopicSlug, colorBySlug]);

  const drilledTopic = drillTopicSlug
    ? topicBySlug.get(drillTopicSlug)
    : undefined;

  useEffect(() => {
    setDrillTopicSlug(null);
  }, [hierarchy]);

  const onDrillTopicChangeRef = useRef(onDrillTopicChange);
  onDrillTopicChangeRef.current = onDrillTopicChange;

  useEffect(() => {
    onDrillTopicChangeRef.current?.(drillTopicSlug);
  }, [drillTopicSlug]);

  useEffect(() => {
    if (drillTopicSlug && !drillSubtopicRoot) {
      setDrillTopicSlug(null);
    }
  }, [drillTopicSlug, drillSubtopicRoot]);

  const layout = useMemo(() => {
    if (width <= 0) {
      return { leaves: [] as LayoutRect[], branches: [] as LayoutRect[] };
    }

    if (drillDown && drillSubtopicRoot) {
      const nodes = layoutTreemapNodes(
        {
          name: "root",
          slug: "root",
          display: drilledTopic?.display ?? "Subtopics",
          size: 0,
          kind: "root",
          children: drillSubtopicRoot.children,
        },
        width,
        height,
      );
      return {
        leaves: nodes.map((node) => ({
          datum: node.data,
          x0: node.x0,
          y0: node.y0,
          x1: node.x1,
          y1: node.y1,
        })),
        branches: [] as LayoutRect[],
      };
    }

    const data = level === "subtopic" ? subtopicData : topicData;

    if ((data.children?.length ?? 0) === 0) {
      return { leaves: [] as LayoutRect[], branches: [] as LayoutRect[] };
    }

    if (level === "subtopic") {
      return layoutGroupedSubtopicTreemap(
        topicData,
        subtopicData,
        width,
        height,
      );
    }

    const nodes = layoutTreemapNodes(topicData, width, height);
    return {
      leaves: nodes.map((node) => ({
        datum: node.data,
        x0: node.x0,
        y0: node.y0,
        x1: node.x1,
        y1: node.y1,
      })),
      branches: [] as LayoutRect[],
    };
  }, [
    width,
    height,
    drillDown,
    drillSubtopicRoot,
    drilledTopic?.display,
    level,
    subtopicData,
    topicData,
  ]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const observer = new ResizeObserver((entries) => {
      const next = entries[0]?.contentRect.width;
      if (next && next > 0) setWidth(next);
    });
    observer.observe(el);
    setWidth(el.clientWidth || 0);
    return () => observer.disconnect();
  }, []);

  const isDrilldownView = drillDown && drillTopicSlug != null;

  if (hierarchy.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        {level === "subtopic" && !drillDown
          ? "No subtopic assignments to visualize."
          : "No accepted topic assignments to visualize."}
      </Typography>
    );
  }

  if (
    !drillDown &&
    level === "subtopic" &&
    (subtopicData.children?.length ?? 0) === 0
  ) {
    return (
      <Typography variant="body2" color="text.secondary">
        No subtopic assignments to visualize.
      </Typography>
    );
  }

  if ((topicData.children?.length ?? 0) === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No accepted topic assignments to visualize.
      </Typography>
    );
  }

  const canDrillTopic = (slug: string) => {
    const topic = topicBySlug.get(slug);
    return topic != null && topicHasDrilldownSubtopics(topic);
  };

  return (
    <Box>
      {drillDown && !compact && (
        <Stack spacing={0.5} sx={{ mb: 1 }}>
          <Breadcrumbs aria-label="Treemap drill-down">
            <Link
              component="button"
              type="button"
              underline={isDrilldownView ? "hover" : "none"}
              color={isDrilldownView ? "primary" : "text.primary"}
              fontWeight={isDrilldownView ? 400 : 600}
              sx={{
                cursor: isDrilldownView ? "pointer" : "default",
                border: 0,
                background: "none",
                p: 0,
              }}
              onClick={() => setDrillTopicSlug(null)}
            >
              Todos
            </Link>
            {isDrilldownView && drilledTopic && (
              <Typography color="text.primary" fontWeight={600} variant="body2">
                {drilledTopic.display}
              </Typography>
            )}
          </Breadcrumbs>
        </Stack>
      )}

      <Box
        ref={containerRef}
        sx={{ width: "100%", height, overflow: "hidden", borderRadius: 1 }}
      >
        {width > 0 && (
          <svg
            width={width}
            height={height}
            role="img"
            aria-label="Topic treemap"
          >
            <g>
              {layout.leaves.map((rect) => {
                const isTopicDrillTarget =
                  drillDown &&
                  !isDrilldownView &&
                  rect.datum.kind === "topic" &&
                  canDrillTopic(rect.datum.slug);

                const isTopicLeafTarget =
                  drillDown &&
                  !isDrilldownView &&
                  rect.datum.kind === "topic" &&
                  leafTopicOpensChunks &&
                  !!onSubtopicClick &&
                  !canDrillTopic(rect.datum.slug);

                const isSubtopicClickTarget =
                  drillDown &&
                  isDrilldownView &&
                  rect.datum.kind === "subtopic" &&
                  !!onSubtopicClick &&
                  !!drilledTopic;

                const isSelectedSubtopic = isSubtopicClickTarget
                  ? selectedSubtopicSlug === rect.datum.slug
                  : isTopicLeafTarget
                    ? selectedSubtopicSlug === `${rect.datum.slug}__general`
                    : false;

                return (
                  <TreemapLeaf
                    key={`leaf:${rect.datum.parentTopicSlug ?? "topic"}:${rect.datum.slug}`}
                    rect={rect}
                    interactive={
                      isTopicDrillTarget ||
                      isTopicLeafTarget ||
                      isSubtopicClickTarget
                    }
                    compact={compact}
                    selected={isSelectedSubtopic}
                    onClick={
                      isTopicDrillTarget
                        ? () => setDrillTopicSlug(rect.datum.slug)
                        : isTopicLeafTarget
                          ? () =>
                              onSubtopicClick?.({
                                topicSlug: rect.datum.slug,
                                subtopicSlug: `${rect.datum.slug}__general`,
                                subtopicDisplay: rect.datum.display,
                              })
                          : isSubtopicClickTarget && drilledTopic
                            ? () =>
                                onSubtopicClick?.({
                                  topicSlug: drilledTopic.slug,
                                  subtopicSlug: rect.datum.slug,
                                  subtopicDisplay: rect.datum.display,
                                })
                            : undefined
                    }
                  />
                );
              })}
              {layout.branches.map((rect) => (
                <TreemapBranch key={`branch:${rect.datum.slug}`} rect={rect} />
              ))}
            </g>
          </svg>
        )}
      </Box>
    </Box>
  );
};

export default TopicTreemap;
